/* ============================================================================
   biinii-core.js — Shared behaviors for Biinii Design (bd-* sections).
   Loaded once per page via <script src="..."> in any bd-* section (browser dedups
   by URL). Currently only ships the reveal-on-scroll observer. Section-specific
   behavior (header sticky, finder preview, gallery) lives in section-bd-*.js.
   ============================================================================ */
(function () {
  'use strict';

  // Reveal-on-scroll: add .is-in when 12% of the element enters the viewport.
  // Fail-safe: after 3s, force-reveal anything still hidden (prevents stuck
  // elements when the observer never fires e.g. content loaded above the fold).
  if (typeof window === 'undefined') return;

  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    document.querySelectorAll('.bd-reveal').forEach(function (el) { el.classList.add('is-in'); });
    return;
  }

  // Wrap class addition in rAF so the browser paints the initial .bd-reveal
  // state at least once before the transition kicks in. Without this, elements
  // already in the viewport at pageload skip the animation (IO fires before
  // the first paint, so the transition has no "from" state to animate from).
  function reveal(el) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('is-in');
      });
    });
  }

  if (!('IntersectionObserver' in window)) {
    // Old browsers: reveal everything immediately, no animation.
    document.querySelectorAll('.bd-reveal').forEach(reveal);
    return;
  }

  // FAIL-SAFE (V3): content is visible by default in CSS. Only now that the
  // JS is alive and IntersectionObserver is available do we opt in to the
  // hidden initial state (html.bd-anim). Crawlers, AI agents and any JS
  // failure keep the page fully readable.
  document.documentElement.classList.add('bd-anim');

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        reveal(entry.target);
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  function observeAll() {
    document.querySelectorAll('.bd-reveal:not(.is-in)').forEach(function (el) { io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll);
  } else {
    observeAll();
  }

  // Re-observe when Shopify theme editor re-renders a section.
  document.addEventListener('shopify:section:load', observeAll);

  // Fail-safe: any .bd-reveal still hidden after 3s gets revealed.
  setTimeout(function () {
    document.querySelectorAll('.bd-reveal:not(.is-in)').forEach(reveal);
  }, 3000);
})();

/* ============================================================================
   Count-up (V3) — anime les nombres des éléments .bd-countup au scroll.
   Progressive enhancement strict : sans JS ou avec reduced-motion, le texte
   final reste tel quel (les valeurs vivent dans le HTML, jamais dans le JS).
   Heuristique : seuls les nombres à décimales ou à ≥ 2 chiffres comptent
   (« 4.9 » et « 700 » oui, le « 5 » de « 4.9 / 5 » non).
   ============================================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var RE = /\d+(?:[.,]\d+)?/g;

  function prep(el) {
    if (el.dataset.bdCountReady) return [];
    el.dataset.bdCountReady = '1';
    var targets = [];
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var text = node.nodeValue;
      RE.lastIndex = 0;
      if (!RE.test(text)) return;
      var frag = document.createDocumentFragment();
      var last = 0;
      text.replace(RE, function (match, offset) {
        var sep = match.indexOf(',') > -1 ? ',' : '.';
        var normalized = match.replace(',', '.');
        var decimals = (normalized.split('.')[1] || '').length;
        if (decimals === 0 && match.length < 2) return match; // « 5 » : statique
        frag.appendChild(document.createTextNode(text.slice(last, offset)));
        var span = document.createElement('span');
        span.className = 'bd-countup__num';
        span.textContent = match;
        span.style.minWidth = match.length + 'ch';
        targets.push({ span: span, el: el, target: parseFloat(normalized), decimals: decimals, sep: sep });
        frag.appendChild(span);
        last = offset + match.length;
        return match;
      });
      frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
    return targets;
  }

  var COUNT_DUR = 700; // durée d'un comptage — et pas du suivant avant la fin

  function run(targets) {
    var dur = COUNT_DUR;
    var t0 = null;
    function tick(t) {
      if (t0 === null) t0 = t;
      var done = true;
      targets.forEach(function (it) {
        var p = Math.min(1, Math.max(0, (t - t0 - (it.delay || 0)) / dur));
        if (p < 1) done = false;
        var e = 1 - Math.pow(1 - p, 3); // ease-out cubic
        var v = (it.target * e).toFixed(it.decimals);
        it.span.textContent = it.sep === ',' ? v.replace('.', ',') : v;
      });
      if (!done) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  var io = new IntersectionObserver(function (entries) {
    // Les éléments qui entrent ensemble forment une vague : chaque nombre
    // démarre 300 ms après le précédent, de gauche à droite (ordre DOM).
    var batch = [];
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      batch = batch.concat(prep(entry.target));
    });
    if (!batch.length) return;
    batch.sort(function (a, b) {
      return a.span.compareDocumentPosition(b.span) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    // Relais avec tuilage : le suivant s'élance quand le précédent entame
    // ses 20 derniers pourcents (créneau = 80 % de la durée d'un comptage).
    // Les éléments data-bd-count-follow="key" ne prennent pas de créneau :
    // ils se synchronisent sur l'élément data-bd-count-key="key".
    var seq = 0;
    var slot = Math.round(COUNT_DUR * 0.8);
    batch.forEach(function (it) {
      if (!it.el.dataset.bdCountFollow) it.delay = seq++ * slot;
    });
    batch.forEach(function (it) {
      var key = it.el.dataset.bdCountFollow;
      if (!key) return;
      var primary = batch.filter(function (p) { return p.el.dataset.bdCountKey === key; })[0];
      it.delay = primary ? primary.delay : 0;
    });
    run(batch);
  }, { threshold: 0.6 });

  function observeAll() {
    document.querySelectorAll('.bd-countup').forEach(function (el) { io.observe(el); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeAll);
  else observeAll();
  document.addEventListener('shopify:section:load', observeAll);
})();
